import { useCallback, useEffect, useState } from 'react';
import { Button } from '@mui/material';
import { Add } from '@mui/icons-material';

import Badge from '@/components/Badge';
import Card from '@/components/Card';
import Stack from '@/components/Stack';
import { Body, Small } from '@/components/Typography';
import { axiosRubyClient } from '@/util/axios/axiosClient';

import AddWorkerBadgeModal from './AddWorkerBadgeModal';

type Props = {
  workerId: number;
};

interface WorkerHeldCertificate {
  id: number;
  name: string;
  display_name_en: string | null;
  certificate_type: 'BADGE' | 'CERTIFICATE';
  image_url: string | null;
}

const errorMessageFrom = (err: any, fallback: string): string => {
  const errors = err?.response?.data?.errors;
  if (Array.isArray(errors) && errors.length > 0) return errors.join(', ');
  return fallback;
};

/**
 * Badges and certificates this worker holds, with grant/revoke.
 *
 * This is the surface that went missing in the WorkerV2 migration: the only
 * grant UI lived on the legacy `Agency/Worker` page, which `/workers/:id` now
 * redirects away from, so a client badge requirement had become impossible to
 * satisfy. The older `CertificatesCard` in this folder is display-only and was
 * never mounted.
 */
const WorkerBadgesCard = ({ workerId }: Props) => {
  const [certificates, setCertificates] = useState<WorkerHeldCertificate[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  // Icons whose URL failed to load. A badge's image_key points at the CDN
  // bucket and some objects 403 there (GRAVY-10805), so rendering the <img>
  // regardless leaves a broken-image glyph beside the name. Track the failures
  // and fall back to name-only rather than showing a broken picture.
  const [brokenIcons, setBrokenIcons] = useState<Record<number, true>>({});

  const load = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await axiosRubyClient.get(
        `/api/admin/workers/${workerId}/certificates`,
      );
      setCertificates(response.data.data || []);
    } catch (err) {
      // Drop any stale list: showing a previous response under an error is
      // worse than showing none.
      setCertificates([]);
      setError(errorMessageFrom(err, 'Failed to load badges.'));
    } finally {
      setIsLoading(false);
    }
  }, [workerId]);

  useEffect(() => {
    load();
  }, [load]);

  /* GRAVY-10806: no Revoke. Removing a badge from a worker is a manual,
     engineer-run operation now — the server route and the GraphQL mutation are
     both gone. The guard behind it only refused while the worker was CHECKED IN
     (JobWorkerStatus::IN_PROGRESS); a worker merely HIRED on a shift whose
     window had already started matched neither that guard nor the upcoming-jobs
     drop scan, so revoking left them rostered on a live badge-gated shift with
     no badge and no notification. */

  // The count is only meaningful once the list is actually known. While loading
  // — or after a failure — `certificates.length` is 0, and rendering "(0)"
  // states something the card cannot support.
  const title =
    isLoading || error
      ? 'Badges & Certificates'
      : `Badges & Certificates (${certificates.length})`;

  return (
    <Card
      title={title}
      actionsDropdown={
        <Button
          size="small"
          startIcon={<Add />}
          variant="outlined"
          onClick={() => setShowAddModal(true)}
        >
          Assign
        </Button>
      }
    >
      {isLoading ? (
        <Card.Section>
          <Small color="lighter">Loading…</Small>
        </Card.Section>
      ) : error ? (
        /* A failed load must NOT fall through to the empty state. "This worker
           holds no badges. They cannot be matched to shifts that require one"
           is a claim about the worker, and an admin acts on it — granting a
           badge they may already hold. When the request failed we do not know
           either way, so say that and offer a retry. */
        <Card.Section>
          <Stack vertical gap={8} align="start">
            <Small color="danger">{error}</Small>
            <Small color="lighter">
              Their badges could not be loaded, so this is not a record of what
              they hold.
            </Small>
            <Button size="small" variant="outlined" onClick={load}>
              Retry
            </Button>
          </Stack>
        </Card.Section>
      ) : certificates.length === 0 ? (
        <Card.Section>
          <Stack vertical gap={12} align="start">
            {/* GRAVY-10797: the card header already carries an Assign button,
                so a second one here was two controls for one action. Keep the
                explanation; drop the duplicate affordance. */}
            <Small color="lighter">
              This worker holds no badges. They cannot be matched to shifts that
              require one. Use Assign above to grant one.
            </Small>
          </Stack>
        </Card.Section>
      ) : (
        certificates.map((cert) => (
          <Card.Section key={cert.id}>
            <Stack gap={12} align="center">
              {cert.image_url && !brokenIcons[cert.id] && (
                <img
                  alt=""
                  src={cert.image_url}
                  style={{ width: 32, height: 32, objectFit: 'contain' }}
                  onError={() =>
                    setBrokenIcons((current) => ({
                      ...current,
                      [cert.id]: true,
                    }))
                  }
                />
              )}
              <Stack vertical gap={2} style={{ flex: 1 }}>
                <Body weight="semibold">
                  {cert.display_name_en || cert.name}
                </Body>
              </Stack>
              <Badge
                label={cert.certificate_type === 'BADGE' ? 'Badge' : 'Cert'}
                status={cert.certificate_type === 'BADGE' ? 'theme' : 'neutral'}
              />
            </Stack>
          </Card.Section>
        ))
      )}

      <AddWorkerBadgeModal
        existingCertificateIds={certificates.map((cert) => cert.id)}
        open={showAddModal}
        workerId={workerId}
        onBadgeAdded={load}
        onClose={() => setShowAddModal(false)}
      />
    </Card>
  );
};

export default WorkerBadgesCard;
